import React from "react";
import { Button, Column, If, makeClassName, Row, TruncatedText, ViewableObserver } from "@hex-insights/core";
import { Icon } from "../../../../../Components/Icon";
import { User } from "../../../../../Utilities";
import { UserFormat } from "../../../../Format";
import * as UserComponents from "../../../User";
import { MessagingUser } from "./utils";
import styles from "./edit-modal.module.css";

const listPageSize = 25;

export type UserRecipientListProps = {
	users: MessagingUser[];
	selectedUserIDs: User["id"][];
	onClickUserRecipient: (id: User["id"]) => void;
	paginationSyncID?: any;
};

export function UserRecipientList({
	users,
	selectedUserIDs,
	onClickUserRecipient,
	paginationSyncID,
}: UserRecipientListProps) {
	const [numShown, setNumShown] = React.useState(listPageSize);
	const incrementNumShown = React.useCallback(() => {
		setNumShown((prev) => prev + listPageSize);
	}, []);
	React.useEffect(() => {
		setNumShown(listPageSize);
	}, [paginationSyncID]);

	return (
		<Column justify="spaced-start" verticalSpacing="0.5rem">
			{users.map((user) => (
				<UserRecipientListButton
					key={user.id}
					user={user}
					onClick={onClickUserRecipient}
					isSelected={selectedUserIDs.includes(user.id)}
				/>
			))}
			<If condition={numShown < users.length}>
				<ViewableObserver onEnterView={incrementNumShown} />
			</If>
		</Column>
	);
}

type UserRecipientListButtonProps = {
	user: MessagingUser;
	isSelected?: boolean;
	onClick: (id: User["id"]) => void;
};

function UserRecipientListButton({ user, isSelected = false, onClick }: UserRecipientListButtonProps) {
	let description = " ";
	if (user.person?.employee) {
		description = user.person?.employee?.jobTitle || "Staff";
	} else if (user.person?.guardian) {
		description = "Parent/Guardian";
	}

	const fullOnClick = React.useCallback(() => {
		onClick(user.id);
	}, [onClick, user.id]);

	return (
		<React.Fragment>
			<Button
				onClick={fullOnClick}
				className={makeClassName(styles["recipient-button"], isSelected ? styles["recipient-button--selected"] : "")}
			>
				<Row justify="space-between" align="center">
					<Row justify="spaced-start" align="center" verticalSpacing="0.375rem">
						<UserComponents.UserWithMessagingStatusIcon user={user} size={34} />

						<Column style={{ width: "100%", overflow: "hidden" }}>
							<TruncatedText className={styles["recipient-button__name"]}>{UserFormat.name(user)}</TruncatedText>
							<TruncatedText className={styles["recipient-button__description"]}>{description}</TruncatedText>
						</Column>
					</Row>

					<If condition={isSelected}>
						<div className={styles["recipient-button__x-button"]}>
							<Icon.X size={14} className="block" />
						</div>
					</If>
				</Row>
			</Button>
		</React.Fragment>
	);
}
